import { create } from 'zustand'

export type FuelSoundType = 'meter' | 'receipt' | 'payment' | 'alert' | 'approval'

export interface FuelStationNotification {
  notificationId: string
  stationId: number
  stationName?: string | null
  employeeId?: string | null
  employeeName?: string | null
  operationType: string
  referenceId?: string | null
  title: string
  message: string
  actionUrl?: string | null
  createdAt: string
  isRead: boolean
  isApproved?: boolean | null
  isHidden?: boolean
  needsAction: boolean
  priority: string
  soundType: FuelSoundType | string
}

export function mapFuelNotification(raw: any): FuelStationNotification {
  return {
    notificationId: String(raw.notificationId ?? raw.NotificationId ?? ''),
    stationId: Number(raw.stationId ?? raw.StationId ?? 0),
    stationName: raw.stationName ?? raw.StationName ?? null,
    employeeId: raw.employeeId ?? raw.EmployeeId ?? null,
    employeeName: raw.employeeName ?? raw.EmployeeName ?? null,
    operationType: String(raw.operationType ?? raw.OperationType ?? ''),
    referenceId: raw.referenceId ?? raw.ReferenceId ?? null,
    title: String(raw.title ?? raw.Title ?? ''),
    message: String(raw.message ?? raw.Message ?? ''),
    actionUrl: raw.actionUrl ?? raw.ActionUrl ?? null,
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? new Date().toISOString()),
    isRead: !!(raw.isRead ?? raw.IsRead),
    isApproved: raw.isApproved ?? raw.IsApproved ?? null,
    isHidden: !!(raw.isHidden ?? raw.IsHidden),
    needsAction: !!(raw.needsAction ?? raw.NeedsAction),
    priority: String(raw.priority ?? raw.Priority ?? 'normal'),
    soundType: String(raw.soundType ?? raw.SoundType ?? 'meter'),
  }
}

interface FuelNotificationState {
  items: FuelStationNotification[]
  unreadCount: number
  live: FuelStationNotification[]
  hydrate: (rows: FuelStationNotification[], unread: number) => void
  incoming: (raw: any) => FuelStationNotification | null
  dismissLive: (id: string) => void
  markLocalRead: (id: string) => void
  markAllLocalRead: () => void
  hideLocal: (id: string) => void
  unhideLocal: (id: string) => void
  deleteLocal: (id: string) => void
}

export const useFuelNotificationStore = create<FuelNotificationState>((set, get) => ({
  items: [],
  unreadCount: 0,
  live: [],
  hydrate: (rows, unread) => set({ items: rows, unreadCount: unread }),
  incoming: (raw) => {
    const row = mapFuelNotification(raw)
    if (!row.notificationId) return null
    if (get().items.some((x) => x.notificationId === row.notificationId)) return null
    set((s) => ({
      items: [row, ...s.items].slice(0, 80),
      unreadCount: s.unreadCount + (row.isRead ? 0 : 1),
      live: [row, ...s.live].slice(0, 6),
    }))
    return row
  },
  dismissLive: (id) => set((s) => ({ live: s.live.filter((x) => x.notificationId !== id) })),
  markLocalRead: (id) =>
    set((s) => ({
      items: s.items.map((x) => (x.notificationId === id ? { ...x, isRead: true } : x)),
      unreadCount: Math.max(0, s.unreadCount - (s.items.find((x) => x.notificationId === id && !x.isRead) ? 1 : 0)),
      live: s.live.filter((x) => x.notificationId !== id),
    })),
  markAllLocalRead: () =>
    set((s) => ({
      items: s.items.map((x) => ({ ...x, isRead: true })),
      unreadCount: 0,
      live: [],
    })),
  hideLocal: (id) =>
    set((s) => ({
      items: s.items.map((x) => (x.notificationId === id ? { ...x, isHidden: true, isRead: true } : x)),
      unreadCount: Math.max(0, s.unreadCount - (s.items.find((x) => x.notificationId === id && !x.isRead) ? 1 : 0)),
      live: s.live.filter((x) => x.notificationId !== id),
    })),
  unhideLocal: (id) =>
    set((s) => ({
      items: s.items.map((x) => (x.notificationId === id ? { ...x, isHidden: false } : x)),
    })),
  deleteLocal: (id) =>
    set((s) => ({
      items: s.items.filter((x) => x.notificationId !== id),
      unreadCount: Math.max(0, s.unreadCount - (s.items.find((x) => x.notificationId === id && !x.isRead) ? 1 : 0)),
      live: s.live.filter((x) => x.notificationId !== id),
    })),
}))
